/**
 * Sends changes kept on this phone while offline (V19.14).
 *
 * Mounted once for the whole app. While someone is signed in it sends their
 * waiting changes (utils/offlineWrites.js) when the app starts, when the
 * phone comes back online, when the app is brought back to the front, and
 * every 30 seconds while anything is still waiting. Draws nothing itself;
 * the person sees a short note when changes go through or are turned down.
 */
import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth, API } from "../../contexts/AuthContext";
import { useTranslation } from "@/i18n";
import { flushWrites, refreshPendingCount, pendingWrites, isOffline } from "../../utils/offlineWrites";

export default function OfflineSync() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const owner = user?.id;

  useEffect(() => {
    if (!owner) {
      refreshPendingCount(null);
      return undefined;
    }
    let alive = true;
    const run = async () => {
      if (isOffline()) return;
      let r;
      try { r = await flushWrites(owner, API); } catch (e) { return; }
      if (!alive || !r) return;
      if (r.refused) toast.error(t("offline.refused", { count: r.refused }));
      else if (r.sent && !r.left) toast.success(t("offline.sent", { count: r.sent }));
    };
    refreshPendingCount(owner).then((n) => { if (n) run(); });
    const onOnline = () => { run(); };
    const onVisible = () => {
      if (document.visibilityState === "visible" && pendingWrites() > 0) run();
    };
    const timer = setInterval(() => { if (pendingWrites() > 0) run(); }, 30000);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [owner]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
